import {Document, Font, Page, Text, StyleSheet, View} from '@react-pdf/renderer'
import {formatGfr, getPatientFullName} from '@/modules/patients/functions'
import {getFullName} from '@/modules/users/functions'
import {getDatetimeFormatter,
  getAge,
  numberFormatter,
  getShortDateFormatter,
  getFullDateFormatter,
  isDefined,
} from '@cypro/ui/utils'
import {DefaultParamType, TFnType, TranslationKey} from '@tolgee/react'
import {ReactNode} from 'react'
import {Patient} from '@cypro/engine/schema'
import {FlatRow, TherapyStep} from './tables/rows'

Font.register({
  family: 'Satoshi',
  src: '/fonts/Satoshi-Variable.ttf',
})

Font.register({
  family: 'Satoshi',
  src: '/fonts/Satoshi-VariableItalic.ttf',
  fontStyle: 'italic',
})

const styles = StyleSheet.create({
  page: {
    paddingTop: 120,
    paddingBottom: 40,
    paddingHorizontal: 24,
    fontSize: 8,
    fontFamily: 'Satoshi',
  },
  header: {
    position: 'absolute',
    top: 20,
    left: 24,
    right: 24,
    fontSize: 12,
    textAlign: 'left',
    fontFamily: 'Satoshi',
  },
  footer: {
    position: 'absolute',
    bottom: 16,
    left: 24,
    right: 24,
    fontSize: 8,
  },
  sectionTitle: {
    marginBottom: 4,
    paddingVertical: 4,
    paddingHorizontal: 6,
    backgroundColor: '#F4F4F5',
  },
  row: {
    flexDirection: 'row',
    borderBottom: '0.5px solid #E4E4E7',
    paddingVertical: 4,
  },
  cellHeader: {
    fontSize: 7,
    fontWeight: 500,
    color: '#A1A1AA',
  },
  cell: {
    fontSize: 7.5,
  },
  cellMedication: {
    fontSize: 7.5,
  },
  cellMuted: {
    fontSize: 6.5,
    color: '#71717A',
  },
})

const COLUMN_WIDTHS = [
  {key: 'time', width: '8%'},
  {key: 'protocol', width: '12%'},
  {key: 'medication', width: '20%'},
  {key: 'dose', width: '15%'},
  {key: 'solution', width: '13%'},
  {key: 'administration', width: '9%'},
  {key: 'prescription', width: '11%'},
  {key: 'status', width: '12%'},
] as const

type TherapyFlatRow = Extract<FlatRow, {type: 'therapy'}>
type Translate = TFnType<DefaultParamType, string, TranslationKey>

function formatDateHeading(dateKey: string) {
  return getFullDateFormatter().format(new Date(Number(dateKey)))
}

function findCurrentStep(steps?: TherapyStep[]) {
  if (!steps?.length) return undefined
  return (
    steps.find(step => step.status === 'in-progress') ??
    steps.find(step => step.status === 'failed') ??
    steps.toReversed().find(step => step.status === 'finished') ??
    steps[0]
  )
}

function TherapyRowContent({row, t}: {row: TherapyFlatRow; t: Translate}) {
  const currentStep = findCurrentStep(row.steps)
  const protocolLabel = row.medicineIndex === 0 ? row.therapy.protocol?.name || '-' : ''
  const cycleDayLabel =
    row.medicineIndex === 0 && row.therapy.cycle !== null && row.therapy.day !== null
      ? t('c_x_d_y', {cycle: row.therapy.cycle, day: row.therapy.day})
      : ''
  const administrationLabel = row.medicineIndex === 0 ? row.therapy.applicationForm?.name || '-' : ''
  const durationLabel = row.medicineIndex === 0 && row.therapy.duration ? `${row.therapy.duration} min` : ''

  return (
    <View style={styles.row} wrap={false}>
      <View style={{width: COLUMN_WIDTHS[0].width}}>
        {row.medicineIndex === 0 && (
          <>
            <Text style={styles.cell}>{row.therapy.time}</Text>
            {row.timeLabel && <Text style={styles.cellMuted}>{row.timeLabel}</Text>}
          </>
        )}
      </View>
      <View style={{width: COLUMN_WIDTHS[1].width}}>
        <Text style={styles.cell}>{protocolLabel}</Text>
        {cycleDayLabel && <Text style={styles.cellMuted}>{cycleDayLabel}</Text>}
      </View>
      <View style={{width: COLUMN_WIDTHS[2].width}}>
        <Text style={styles.cellMedication}>{row.medication}</Text>
      </View>
      <View style={{width: COLUMN_WIDTHS[3].width}}>
        <Text style={styles.cellMedication}>{row.medicationAmount}</Text>
        {row.medicationProportion?.trim() && <Text style={styles.cellMuted}>{row.medicationProportion.trim()}</Text>}
        {row.calculationMethodLabel && <Text style={styles.cellMuted}>{row.calculationMethodLabel}</Text>}
        {row.calculationValueLabel && <Text style={styles.cellMuted}>{row.calculationValueLabel}</Text>}
      </View>
      <View style={{width: COLUMN_WIDTHS[4].width}}>
        <Text style={styles.cell}>
          {`${row.therapy.volumeMode ? `${t(row.therapy.volumeMode)} ` : ''}${row.therapy?.carrierSolution?.name || '-'}`}
        </Text>
        {row.solutionVolume && <Text style={styles.cellMuted}>{row.solutionVolume}</Text>}
      </View>
      <View style={{width: COLUMN_WIDTHS[5].width}}>
        <Text style={styles.cell}>{administrationLabel}</Text>
        {durationLabel && <Text style={styles.cellMuted}>{durationLabel}</Text>}
      </View>
      <View style={{width: COLUMN_WIDTHS[6].width}}>
        <Text style={styles.cell}>{row.medicineIndex === 0 ? row.therapy.internalId || '-' : ''}</Text>
      </View>
      <View style={{width: COLUMN_WIDTHS[7].width}}>
        {currentStep && row.medicineIndex === 0 && (
          <>
            <Text style={styles.cell}>{row.therapy.status ? t(row.therapy.status) : ''}</Text>
            {row.therapy.doctorValidatedAt && (
              <Text style={styles.cellMuted}>{getDatetimeFormatter().format(row.therapy.doctorValidatedAt)}</Text>
            )}
            {row.therapy.pharmacistValidatedAt && (
              <Text style={styles.cellMuted}>{getDatetimeFormatter().format(row.therapy.pharmacistValidatedAt)}</Text>
            )}
          </>
        )}
      </View>
    </View>
  )
}

function renderFlatRow(row: Exclude<FlatRow, {type: 'date-header'}>, index: number, t: Translate) {
  if (row.type === 'note') {
    return (
      <View key={`note-${row.therapy.id}`} style={styles.row} wrap={false}>
        <Text style={[styles.cell, {width: COLUMN_WIDTHS[0].width}]}>{row.therapy.time}</Text>
        <Text style={[styles.cell, {fontStyle: 'italic', color: '#71717A', width: '80%'}]}>{row.therapy.note}</Text>
      </View>
    )
  }

  if (row.type === 'medicine-note') {
    return (
      <View key={`medicine-note-${row.medicineId}`} style={styles.row} wrap={false}>
        <Text style={[styles.cell, {width: COLUMN_WIDTHS[0].width}]} />
        <Text style={[styles.cell, {fontStyle: 'italic', color: '#71717A', width: '80%'}]}>{row.notes}</Text>
      </View>
    )
  }

  return <TherapyRowContent key={`therapy-${row.therapy.id}-${index}`} row={row} t={t} />
}

export function TherapyTablePrint({patient, flatRows, t}: {patient: Patient; flatRows: FlatRow[]; t: Translate}) {
  const characteristics = patient.characteristics?.[0]

  // a printed selection can span several cycles of the same protocol, so collect the real cycles per protocol
  const protocolInfoMap = new Map<
    string,
    {protocol: NonNullable<TherapyFlatRow['therapy']['protocol']>; cycles: number | null; cycleNumbers: Set<number>}
  >()

  for (const row of flatRows) {
    if (row.type !== 'therapy') continue
    const protocolId = row.therapy.patientProtocolId
    const protocol = row.therapy.protocol
    if (!protocolId || !protocol) continue

    const entry = protocolInfoMap.get(protocolId) ?? {
      protocol,
      cycles: row.therapy.patientProtocol?.cycles ?? protocol.totalCycles,
      cycleNumbers: new Set<number>(),
    }
    if (row.therapy.cycle != null) entry.cycleNumbers.add(row.therapy.cycle)
    protocolInfoMap.set(protocolId, entry)
  }

  const protocolInfo = [...protocolInfoMap.values()]

  const headerInfo = [
    isDefined(patient.birthDate)
      ? `${getShortDateFormatter().format(patient.birthDate)} (${getAge(patient.birthDate)} ${t('yrs')})`
      : null,
    patient.hospitalPatientId ? `${t('hospital_patient_id')}: ${patient.hospitalPatientId}` : null,
    patient.identifiers?.[0]?.value || null,
    characteristics?.diagnosis?.name,
    getFullName(patient.responsibleDoctor, 'last-first') || null,
  ].filter(Boolean)

  const gfrValue = patient.gfrs?.[0]?.gfr

  const clinicalInfo = [
    {
      label: t('characteristics.weight'),
      value: characteristics?.weight ? numberFormatter.format(characteristics.weight) : '-',
    },
    {
      label: t('characteristics.height'),
      value: characteristics?.height ? numberFormatter.format(characteristics.height) : '-',
    },
    {label: t('characteristics.bsa'), value: characteristics?.bsa ? numberFormatter.format(characteristics.bsa) : '-'},
    {label: t('characteristics.gfr'), value: formatGfr(gfrValue)},
  ]

  const headerText = [
    headerInfo.join('   |   '),
    protocolInfo.length
      ? `${t('therapy')}: ${protocolInfo
          .map(({protocol, cycles, cycleNumbers}) => {
            const cycleLabel = cycleNumbers.size > 0 ? [...cycleNumbers].join(', ') : 1
            const cycleInfo =
              cycles != null
                ? ` (${t('protocol.cycle_of', {
                    currentCycle: cycleLabel,
                    totalCycles: cycles,
                    days: protocol.daysRepetition ?? '-',
                  })})`
                : ''
            return `${protocol.name}${cycleInfo}`
          })
          .join(', ')}`
      : '',
    clinicalInfo.length ? clinicalInfo.map(item => `${item.label}: ${item.value}`).join('   ') : '',
  ]
    .filter(Boolean)
    .join('\n')

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={styles.page}>
        <View style={styles.header} fixed>
          <View style={{display: 'flex', flexDirection: 'column'}}>
            <View style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
              <Text style={{fontWeight: 'semibold', fontSize: '14px'}}>{getPatientFullName(patient)}</Text>
              <Text style={{fontWeight: 'normal', fontSize: '10px', color: '#52525B'}}>
                {t('generated_date', {date: getDatetimeFormatter().format(new Date())})}
              </Text>
            </View>

            {headerText && <Text style={{fontSize: '10px', color: '#52525B'}}>{headerText}</Text>}
          </View>
        </View>

        <View style={styles.footer} fixed>
          <View style={{display: 'flex', flexDirection: 'row', justifyContent: 'flex-end'}}>
            <Text
              style={{color: '#52525B'}}
              render={({pageNumber, totalPages}) => `Page ${pageNumber} of ${totalPages}`}
            />
          </View>
        </View>

        <View style={styles.row} fixed>
          <Text style={[styles.cellHeader, {width: COLUMN_WIDTHS[0].width}]}>{t('therapy_sequence.time')}</Text>
          <Text style={[styles.cellHeader, {width: COLUMN_WIDTHS[1].width}]}>{t('protocol')}</Text>
          <Text style={[styles.cellHeader, {width: COLUMN_WIDTHS[2].width}]}>
            {t('therapy_sequence.table.medication')}
          </Text>
          <Text style={[styles.cellHeader, {width: COLUMN_WIDTHS[3].width}]}>{t('dose')}</Text>
          <Text style={[styles.cellHeader, {width: COLUMN_WIDTHS[4].width}]}>{t('solution')}</Text>
          <Text style={[styles.cellHeader, {width: COLUMN_WIDTHS[5].width}]}>
            {t('therapy_sequence.table.administration')}
          </Text>
          <Text style={[styles.cellHeader, {width: COLUMN_WIDTHS[6].width}]}>{t('prescription')}</Text>
          <Text style={[styles.cellHeader, {width: COLUMN_WIDTHS[7].width}]}>{t('status')}</Text>
        </View>

        {(() => {
          const elements: ReactNode[] = []
          let index = 0

          while (index < flatRows.length) {
            const row = flatRows[index]

            if (row.type === 'date-header') {
              const nextRow = flatRows[index + 1]
              const groupsWithNext = nextRow && nextRow.type !== 'date-header'

              elements.push(
                <View key={`date-${row.dateKey}`} wrap={false}>
                  <Text style={styles.sectionTitle}>{formatDateHeading(row.dateKey)}</Text>
                  {groupsWithNext && renderFlatRow(nextRow, index + 1, t)}
                </View>
              )
              index += groupsWithNext ? 2 : 1
              continue
            }

            elements.push(renderFlatRow(row, index, t))
            index += 1
          }

          return elements
        })()}
      </Page>
    </Document>
  )
}
